export const DOSHA_LABELS = {
  vata:  'Atmospheric',
  pitta: 'Driven',
  kapha: 'Stable',
};

const SEP = ' · '; // ' · ' with en-spaces

function norm(d) { return (d || '').toLowerCase().trim(); }
function labelFor(d) { return DOSHA_LABELS[norm(d)] || d; }

// Accepts any of:
//   'vata-pitta' | 'Vata' | 'Pitta-Kapha'   (DB string format)
//   'VP' | 'PV' | 'VK' | 'KV' | 'PK' | 'KP' (short code)
//   { dominant: 'vata', secondary: 'pitta' }
//   { vata: 70, pitta: 25, kapha: 5 }         (raw scores)
export function formatConstitution(input) {
  if (!input) return '';

  // Raw scores object
  if (typeof input === 'object' && !Array.isArray(input) && 'vata' in input) {
    const sorted = ['vata', 'pitta', 'kapha']
      .map(k => [k, input[k] || 0])
      .sort((a, b) => b[1] - a[1]);
    const [[t, tv], [s, sv], [r, rv]] = sorted;
    if (tv < 45 && (tv - sv) < 10 && (sv - rv) < 10) {
      return [t, s, r].map(labelFor).join(SEP); // tridoshic
    }
    if (sv < 20 || (tv - sv) > 35) return labelFor(t); // pure
    return [t, s].map(labelFor).join(SEP);
  }

  // { dominant, secondary }
  if (typeof input === 'object' && 'dominant' in input) {
    const d = labelFor(input.dominant);
    return input.secondary ? d + SEP + labelFor(input.secondary) : d;
  }

  // Short code 'VP', 'PK', etc.
  const CODE = { V: 'vata', P: 'pitta', K: 'kapha' };
  if (typeof input === 'string' && /^[VPK]{2,3}$/i.test(input.trim())) {
    return input.trim().toUpperCase().split('').map(c => labelFor(CODE[c])).join(SEP);
  }

  // DB string 'vata-pitta', 'Kapha', etc.
  const parts = (input || '').split(/[-–—]+/).map(s => s.trim()).filter(Boolean);
  return parts.map(labelFor).join(SEP);
}

export function singleLabel(dosha) {
  return DOSHA_LABELS[norm(dosha)] || '';
}

// Returns ordered array of brand labels: ['Atmospheric', 'Driven'] (dominant first).
// Works with any dosha_type format the DB might store.
export function constitutionParts(input) {
  if (!input) return [];

  // Raw scores object
  if (typeof input === 'object' && !Array.isArray(input) && 'vata' in input) {
    const sorted = ['vata', 'pitta', 'kapha']
      .map(k => [k, input[k] || 0])
      .sort((a, b) => b[1] - a[1]);
    const [[t, tv], [s, sv]] = sorted;
    if (tv < 45 && (tv - sv) < 10) return sorted.map(([k]) => labelFor(k));
    if (sv < 20 || (tv - sv) > 35) return [labelFor(t)];
    return [labelFor(t), labelFor(s)];
  }

  // { dominant, secondary }
  if (typeof input === 'object' && 'dominant' in input) {
    const parts = [labelFor(input.dominant)];
    if (input.secondary) parts.push(labelFor(input.secondary));
    return parts;
  }

  // Short code 'VP', 'PK', 'VPK', etc.
  const CODE = { V: 'vata', P: 'pitta', K: 'kapha' };
  if (typeof input === 'string' && /^[VPK]{2,3}$/i.test(input.trim())) {
    return input.trim().toUpperCase().split('').map(c => labelFor(CODE[c]));
  }

  // DB string 'vata-pitta', 'Kapha', etc.
  return (input || '').split(/[-–—]+/).map(s => s.trim()).filter(Boolean).map(labelFor);
}
